<template>
  <div>

    <!-- ═══════════════════════ MOBILE ═══════════════════════ -->
    <div v-if="isMobileOrTablet">
      <appHeader></appHeader>
      <div class="container-fluid account p-3 mt-2 pt-4">

        <!-- Stepper -->
        <div class="cart-stepper mb-4">
          <div class="cs-step done">
            <div class="cs-dot"><font-awesome-icon icon="check" /></div>
            <span>Items</span>
          </div>
          <div class="cs-line cs-line-done"></div>
          <div class="cs-step done">
            <div class="cs-dot"><font-awesome-icon icon="check" /></div>
            <span>Shipping</span>
          </div>
          <div class="cs-line cs-line-done"></div>
          <div class="cs-step done">
            <div class="cs-dot"><font-awesome-icon icon="check" /></div>
            <span>Summary</span>
          </div>
        </div>

        <!-- Loading -->
        <transition v-if="isOrderingInProgress" appear name="fade">
          <div class="os-loading">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Setting up your order...</span>
            </div>
            <p class="os-loading-text">Processing your order...</p>
          </div>
        </transition>

        <transition v-else appear name="fade">
          <div>
            <!-- Success / error banner -->
            <div class="os-banner" :class="orderPlacedSuccessfully ? 'os-banner-success' : 'os-banner-danger'">
              <div class="os-banner-icon" :class="orderPlacedSuccessfully ? 'os-icon-success' : 'os-icon-danger'">
                <font-awesome-icon :icon="orderPlacedSuccessfully ? 'check' : 'times'" />
              </div>
              <div>
                <div class="os-banner-title">
                  {{ userName }}, your order was
                  {{ orderPlacedSuccessfully ? 'successfully submitted' : 'not submitted. Please try again.' }}
                </div>
              </div>
            </div>

            <!-- Items ready -->
            <div v-if="productsReady.length > 0" class="os-section mb-3">
              <div class="os-section-header os-header-success">
                <font-awesome-icon icon="gift" class="mr-1" />
                <span>Items ready to use</span>
                <span class="os-count-badge os-count-success">{{ productsReady.length }}</span>
              </div>
              <div v-for="product in productsReady" :key="product.id" class="os-product-row">
                <router-link
                  :to="{ name: 'Product', params: { productId: product.productId, productCategoryId: product.categoryID } }"
                  class="os-prod-img"
                >
                  <img :alt="product.name" :src="product.imageUrl" />
                </router-link>
                <div class="os-prod-body">
                  <router-link
                    :to="{ name: 'Product', params: { productId: product.productId, productCategoryId: product.categoryID } }"
                    class="os-prod-name"
                  >{{ product.name }}</router-link>

                  <div v-if="product.isCard" class="os-sent-badge">
                    <font-awesome-icon icon="check" />
                    Sent to {{ product.recipientEmail }}
                  </div>

                  <div v-if="product.isCard && product.claimCode" class="os-redemption-box">
                    <div class="os-redemption-label">Redemption code</div>
                    <div class="os-redemption-val">{{ product.claimCode }}</div>
                  </div>
                  <div v-else-if="product.isCard && product.claimUrl" class="os-redemption-box">
                    <div class="os-redemption-label">Redemption URL</div>
                    <a :href="product.claimUrl" target="_blank" class="os-redemption-val">Open link</a>
                  </div>
                  <div v-else-if="product.isCard && product.claimImageUrl" class="os-redemption-box">
                    <div class="os-redemption-label">Redemption barcode</div>
                    <img :src="product.claimImageUrl" class="os-barcode-img" />
                  </div>
                  <div v-else-if="product.isCard && product.claimDate" class="os-redemption-box">
                    <div class="os-redemption-label">Redemption date</div>
                    <div class="os-redemption-val">{{ product.claimDate }}</div>
                  </div>

                  <div v-if="product.isCard" class="text-sm mt-2">
                    <b-link v-b-toggle="'m-collapse-b-' + product.id">
                      <font-awesome-icon class="mr-1" icon="angle-right" />
                      Read instructions
                    </b-link>
                    <b-collapse :id="'m-collapse-b-' + product.id" class="w-100 mt-2">
                      <div class="instructions p-2 rounded" v-html="product.instructionsTextHtml"></div>
                    </b-collapse>
                  </div>

                  <div class="os-meta-row">
                    <span class="os-chip os-chip-value">{{ product.points | numToLocale }} {{ product.currency }}</span>
                    <span class="os-chip os-chip-order">Order #{{ product.orderNumber }}</span>
                    <span class="os-chip">Qty: {{ product.quantity | numToLocale }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Items processing -->
            <div v-if="productsProcessing.length > 0" class="os-section mb-3">
              <div class="os-section-header os-header-gray">
                <font-awesome-icon icon="clock" class="mr-1" />
                <span>Items being processed</span>
                <span class="os-count-badge os-count-gray">{{ productsProcessing.length }}</span>
              </div>
              <div v-for="product in productsProcessing" :key="product.id" class="os-product-row">
                <router-link
                  :to="{ name: 'Product', params: { productId: product.productId } }"
                  class="os-prod-img"
                >
                  <img :alt="product.name" :src="product.imageUrl" />
                </router-link>
                <div class="os-prod-body">
                  <router-link
                    :to="{ name: 'Product', params: { productId: product.productId } }"
                    class="os-prod-name"
                  >{{ product.name }}</router-link>
                  <div v-if="product.isCard" class="os-processing-note">
                    This card will be sent to your email. Please check within two business days.
                  </div>
                  <div v-else class="os-processing-note">
                    Please allow up to 4 weeks for delivery.
                  </div>
                  <div class="os-meta-row">
                    <span class="os-chip os-chip-value">{{ (product.points + product.supplierSaleTax) | numToLocale }} pts</span>
                    <span class="os-chip os-chip-order">Order #{{ product.orderNumber }}</span>
                    <span class="os-chip">Qty: {{ product.quantity | numToLocale }}</span>
                  </div>
                  <div v-if="product.supplierSaleTax > 0" class="os-tax-note">
                    Product: {{ product.points }} pts · Sales tax: {{ product.supplierSaleTax }} pts
                  </div>
                </div>
              </div>
            </div>

            <!-- Pending approval -->
            <div v-if="productsPendingApproval.length > 0" class="os-section mb-3">
              <div class="os-section-header os-header-gray">
                <font-awesome-icon icon="clock" class="mr-1" />
                <span>Items pending approval from your administrator</span>
              </div>
              <div v-for="product in productsPendingApproval" :key="product.id" class="os-product-row">
                <router-link
                  :to="{ name: 'Product', params: { productId: product.productId } }"
                  class="os-prod-img"
                >
                  <img :alt="product.name" :src="product.imageUrl" />
                </router-link>
                <div class="os-prod-body">
                  <router-link
                    :to="{ name: 'Product', params: { productId: product.productId } }"
                    class="os-prod-name"
                  >{{ product.name }}</router-link>
                  <div class="os-meta-row">
                    <span class="os-chip">Qty: {{ product.quantity | numToLocale }}</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </transition>

        <!-- Continue shopping -->
        <div class="os-continue-wrap">
          <router-link class="os-continue-btn" to="/shop">
            <font-awesome-icon icon="cart-plus" class="mr-1" />
            Continue shopping
          </router-link>
        </div>

        <!-- Help section -->
        <div class="os-help-section">
          <div class="os-help-title">What's next?</div>
          <div class="os-tips-list">
            <div class="os-tip-card">
              <div class="os-tip-icon"><font-awesome-icon icon="rocket" /></div>
              <div>Multiple items may ship separately with different tracking numbers.</div>
            </div>
            <div class="os-tip-card">
              <div class="os-tip-icon"><font-awesome-icon icon="rocket" /></div>
              <div>If you don't see your gift card, <strong>check your spam folder.</strong></div>
            </div>
          </div>
          <div class="os-contact-card">
            <strong>Need Help?</strong><br />
            <ul>
              <li>Check the “Customer Service/Tracking” tab for tracking numbers.</li>
              <li>If you do not receive your gift card email(s) within 24 hours, or need further assistance, please contact us at support@divvyengagement.com</li>
            </ul>
          </div>
        </div>

      </div>
      <appFooter></appFooter>
    </div>

    <!-- ═══════════════════════ DESKTOP ═══════════════════════ -->
    <div v-else class="mt-2">
      <appHeader></appHeader>
      <div class="container-fluid account p-4">

        <!-- Stepper -->
        <div class="cart-stepper mb-4">
          <div class="cs-step done">
            <div class="cs-dot"><font-awesome-icon icon="check" /></div>
            <span>View items</span>
          </div>
          <div class="cs-line cs-line-done"></div>
          <div class="cs-step done">
            <div class="cs-dot"><font-awesome-icon icon="check" /></div>
            <span>Shipping information</span>
          </div>
          <div class="cs-line cs-line-done"></div>
          <div class="cs-step done">
            <div class="cs-dot"><font-awesome-icon icon="check" /></div>
            <span>Order summary</span>
          </div>
        </div>

        <!-- Loading -->
        <transition v-if="isOrderingInProgress" appear name="fade">
          <div class="os-loading">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Setting up your order...</span>
            </div>
            <p class="os-loading-text">Processing your order...</p>
          </div>
        </transition>

        <transition v-else appear name="fade">
          <div class="row d-flex justify-content-center">
            <div class="col-lg-10 col-md-12 mx-auto">

              <!-- Success / error banner -->
              <div class="os-banner mb-4" :class="orderPlacedSuccessfully ? 'os-banner-success' : 'os-banner-danger'">
                <div class="os-banner-icon" :class="orderPlacedSuccessfully ? 'os-icon-success' : 'os-icon-danger'">
                  <font-awesome-icon :icon="orderPlacedSuccessfully ? 'check' : 'times'" />
                </div>
                <div>
                  <div class="os-banner-title">
                    {{ userName }}, your order was
                    {{ orderPlacedSuccessfully ? 'successfully submitted' : 'not submitted. Please try again.' }}
                  </div>
                  <div v-if="orderPlacedSuccessfully" class="os-banner-sub">
                    A confirmation has been sent to your email address.
                  </div>
                </div>
              </div>

              <!-- Items ready -->
              <div v-if="productsReady.length > 0" class="os-section mb-4">
                <div class="os-section-header os-header-success">
                  <font-awesome-icon icon="gift" class="mr-1" />
                  <span>Items ready to use</span>
                  <span class="os-count-badge os-count-success">{{ productsReady.length }}</span>
                </div>
                <div v-for="product in productsReady" :key="product.id" class="os-product-row">
                  <router-link
                    :to="{ name: 'Product', params: { productId: product.productId } }"
                    class="os-prod-img"
                  >
                    <img :alt="product.name" :src="product.imageUrl" />
                  </router-link>
                  <div class="os-prod-body">
                    <router-link
                      :to="{ name: 'Product', params: { productId: product.productId } }"
                      class="os-prod-name"
                    >{{ product.name }}</router-link>

                    <div v-if="product.isCard" class="os-sent-badge">
                      <font-awesome-icon icon="check" />
                      Sent to {{ product.recipientEmail }}
                    </div>

                    <div v-if="product.isCard && product.claimCode" class="os-redemption-box">
                      <div class="os-redemption-label">Redemption code</div>
                      <div class="os-redemption-val">{{ product.claimCode }}</div>
                    </div>
                    <div v-else-if="product.isCard && product.claimUrl" class="os-redemption-box">
                      <div class="os-redemption-label">Redemption URL</div>
                      <a :href="product.claimUrl" target="_blank" class="os-redemption-val">Open link</a>
                    </div>
                    <div v-else-if="product.isCard && product.claimImageUrl" class="os-redemption-box">
                      <div class="os-redemption-label">Redemption barcode</div>
                      <img :src="product.claimImageUrl" class="os-barcode-img" />
                    </div>
                    <div v-else-if="product.isCard && product.claimDate" class="os-redemption-box">
                      <div class="os-redemption-label">Redemption date</div>
                      <div class="os-redemption-val">{{ product.claimDate }}</div>
                    </div>

                    <div v-if="product.isCard" class="text-sm mt-2">
                      <b-link v-b-toggle="'collapse-b-' + product.id">
                        <font-awesome-icon class="mr-1" icon="angle-right" />
                        Read instructions
                      </b-link>
                      <b-collapse :id="'collapse-b-' + product.id" class="w-100 mt-2">
                        <div class="instructions p-2 rounded" v-html="product.instructionsTextHtml"></div>
                      </b-collapse>
                    </div>

                    <div class="os-meta-row">
                      <span class="os-chip os-chip-value">{{ product.points | numToLocale }} {{ product.currency }}</span>
                      <span class="os-chip os-chip-order">Order #{{ product.orderNumber }}</span>
                      <span class="os-chip">Qty: {{ product.quantity | numToLocale }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Items processing -->
              <div v-if="productsProcessing.length > 0" class="os-section mb-4">
                <div class="os-section-header os-header-gray">
                  <font-awesome-icon icon="clock" class="mr-1" />
                  <span>Items being processed</span>
                  <span class="os-count-badge os-count-gray">{{ productsProcessing.length }}</span>
                </div>
                <div v-for="product in productsProcessing" :key="product.id" class="os-product-row">
                  <router-link
                    :to="{ name: 'Product', params: { productId: product.productId } }"
                    class="os-prod-img"
                  >
                    <img :alt="product.name" :src="product.imageUrl" />
                  </router-link>
                  <div class="os-prod-body">
                    <router-link
                      :to="{ name: 'Product', params: { productId: product.productId } }"
                      class="os-prod-name"
                    >{{ product.name }}</router-link>
                    <div v-if="product.isCard" class="os-processing-note">
                      This card will be sent to your email. Please check within two business days.
                    </div>
                    <div v-else class="os-processing-note">
                      Please allow up to 4 weeks for delivery.
                    </div>
                    <div class="os-meta-row">
                      <span class="os-chip os-chip-value">{{ (product.points + product.supplierSaleTax) | numToLocale }} pts</span>
                      <span class="os-chip os-chip-order">Order #{{ product.orderNumber }}</span>
                      <span class="os-chip">Qty: {{ product.quantity | numToLocale }}</span>
                    </div>
                    <div v-if="product.supplierSaleTax > 0" class="os-tax-note">
                      Product: {{ product.points }} pts · Sales tax: {{ product.supplierSaleTax }} pts
                    </div>
                  </div>
                </div>
              </div>

              <!-- Pending approval -->
              <div v-if="productsPendingApproval.length > 0" class="os-section mb-4">
                <div class="os-section-header os-header-gray">
                  <font-awesome-icon icon="clock" class="mr-1" />
                  <span>Items pending approval from your administrator</span>
                </div>
                <div v-for="product in productsPendingApproval" :key="product.id" class="os-product-row">
                  <router-link
                    :to="{ name: 'Product', params: { productId: product.productId } }"
                    class="os-prod-img"
                  >
                    <img :alt="product.name" :src="product.imageUrl" />
                  </router-link>
                  <div class="os-prod-body">
                    <router-link
                      :to="{ name: 'Product', params: { productId: product.productId } }"
                      class="os-prod-name"
                    >{{ product.name }}</router-link>
                    <div class="os-meta-row">
                      <span class="os-chip">Qty: {{ product.quantity | numToLocale }}</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </transition>

        <!-- Continue shopping -->
        <div class="os-continue-wrap">
          <router-link class="os-continue-btn" to="/shop">
            <font-awesome-icon icon="cart-plus" class="mr-2" />
            Continue shopping
          </router-link>
        </div>

        <!-- Help section -->
        <div class="os-help-section">
          <div class="os-help-title">Thank you for your order! What's next?</div>
          <div class="os-tips-grid">
            <div class="os-tip-card">
              <div class="os-tip-icon"><font-awesome-icon icon="rocket" /></div>
              <div>If you ordered more than one item, there may be multiple packages with different tracking numbers.</div>
            </div>
            <div class="os-tip-card">
              <div class="os-tip-icon"><font-awesome-icon icon="rocket" /></div>
              <div><strong>Tracking is not always updated immediately.</strong> Access tracking in the "Customer Service/Tracking" tab under
                <router-link :to="{ name: 'AccountInformation' }">My account</router-link>.</div>
            </div>
            <div class="os-tip-card">
              <div class="os-tip-icon"><font-awesome-icon icon="rocket" /></div>
              <div>If you don't see your gift card in your email, <strong>please check your spam folder</strong> as cards are occasionally flagged as promotional.</div>
            </div>
          </div>
          <div class="os-contact-card">
            <strong>Need Help?</strong><br />
            <ul>
              <li>Check the “Customer Service/Tracking” tab for tracking numbers.</li>
              <li>If you do not receive your gift card email(s) within 24 hours, or need further assistance, please contact us at support@divvyengagement.com</li>
            </ul>
            <!--<strong>For fastest service</strong><br />
            Find your order status in
            <router-link :to="{ name: 'OrderTracking' }">My orders</router-link>
            &nbsp;·&nbsp; Email us at
            <a href="mailto:customerservice@divvyengagement.com">customerservice@divvyengagement.com</a><br />
            Customer support hours: <strong>M–F, 8 AM – 4 PM Pacific Time</strong>-->
          </div>
        </div>

      </div>
      <appFooter></appFooter>
    </div>

  </div>
</template>
<script>
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import { isMobile, isTablet } from "mobile-device-detect";
import { mapState } from "vuex";

export default {
  components: {
    appHeader: Header,
    appFooter: Footer,
  },
  data() {
    return {
      checkoutProgressMax: 100,
      checkoutProgressCurrent: 100,

      isOrderingInProgress: true,
      orderPlacedSuccessfully: true,

      // TODO physicalproduct and giftcard should each be a separate component
      productsReady: [],
      productsProcessing: [],
      productsPendingApproval: [],
    };
  },
  computed: {
    ...mapState({
      userName: (state) => state.user.profile.firstName,
      payTaxes: (state) => state.cart.usePointsToPayTaxes,
      orderEmailAddress: (state) => state.cart.orderEmailAddress,
    }),
    isMobileOrTablet: function() {
      return isMobile || isTablet;
    },
  },
  beforeMount() {
    this.scrollToTop();
  },
  methods: {
    scrollToTop() {
      window.scrollTo(0, 0);
    },
    addPhysicalProduct(targetArray, physicalProduct) {
      let newProduct = {
        orderNumber: physicalProduct.orderNumber,
        name: physicalProduct.name,
        productId: physicalProduct.productId,
        imageUrl: physicalProduct.imageUrl,
        quantity: physicalProduct.quantity,
        points: physicalProduct.points,
        supplierSaleTax: physicalProduct.supplierSaleTax,
        currency: "pts",
        isCard: false,
      };
      targetArray.push(newProduct);
    },
    addDigitalGiftCard(targetArray, digitalGiftCard) {
      let newProduct = {
        orderNumber: digitalGiftCard.orderNumber,
        name: digitalGiftCard.name,
        productId: digitalGiftCard.productId,
        imageUrl: digitalGiftCard.imageUrl,
        quantity: digitalGiftCard.quantity,
        points: digitalGiftCard.value,
        currency: digitalGiftCard.currencyCode,
        isCard: true,
        recipientEmail: digitalGiftCard.emailedAddress,
        instructionsTextHtml: digitalGiftCard.credentialInstructionsHtml,
      };
      if (digitalGiftCard.credentialType === "text") {
        newProduct.claimCode = digitalGiftCard.credentialValue;
      } else if (digitalGiftCard.credentialType === "url") {
        newProduct.claimUrl = digitalGiftCard.credentialValue;
      } else if (digitalGiftCard.credentialType === "barcode") {
        newProduct.claimImageUrl = digitalGiftCard.credentialValue;
      } else if (digitalGiftCard.credentialType === "date") {
        newProduct.claimDate = digitalGiftCard.credentialValue;
      }

      targetArray.push(newProduct);
    },
    showSuccess: function(message, onClose) {
      this.$bvModal
        .msgBoxOk(message, {
          size: "sm",
          headerClass: "p-2 border-bottom-0",
          footerClass: "p-2 border-top-0",
          centered: true,
        })
        .then(() => {
          onClose();
        });
    },
    showError: function(message) {
      this.$bvModal.msgBoxOk(message, {
        size: "sm",
        headerClass: "p-2 border-bottom-0",
        footerClass: "p-2 border-top-0",
        okVariant: "danger",
        centered: true,
      });
    },
  },
  created() {
    this.$store
      .dispatch("cart/orderCart", {
        payTaxes: this.payTaxes,
        email: this.orderEmailAddress,
      })
      .then((response) => {
        this.isOrderingInProgress = false;
        if (response.isCartValid) {
          response.digitalGiftCards.forEach((element) => {
            if (element.status === "FULFILLED") {
              this.addDigitalGiftCard(this.productsReady, element);
            }
            if (element.status === "ORDERED") {
              this.addDigitalGiftCard(this.productsProcessing, element);
            } else if (element.status === "PENDING_APPROVAL") {
              this.addDigitalGiftCard(this.productsPendingApproval, element);
            }
          });
          response.physicalProducts.forEach((element) => {
            if (element.status === "FULFILLED") {
              this.addPhysicalProduct(this.productsReady, element);
            } else if (element.status === "ORDERED") {
              this.addPhysicalProduct(this.productsProcessing, element);
            } else if (element.status === "PENDING_APPROVAL") {
              this.addPhysicalProduct(this.productsPendingApproval, element);
            }
          });
        } else {
          this.orderPlacedSuccessfully = false;
          // TODO have some specific UI to tell this to the user (not just a modal)
          this.showError(response.errorsArray.join("\n"));
        }
      })
      .catch(() => {
        this.orderPlacedSuccessfully = false;
        this.showError("Failed to order cart contents. Please contact Customer Service.");
      });
  },
};
</script>
<style lang="scss">

/* ── Stepper ── */
.cart-stepper { display: flex; align-items: center; }
.cs-step { display: flex; align-items: center; gap: 7px; font-size: 13px; color: $gray-500; }
.cs-step.done { color: $success; font-weight: 500; }
.cs-dot {
  width: 22px; height: 22px; border-radius: 50%;
  border: 2px solid $gray-300;
  display: flex; align-items: center; justify-content: center;
  font-size: 9px; font-weight: 500; background: #fff; color: $gray-500; flex-shrink: 0;
}
.cs-step.done .cs-dot { border-color: $success; color: #fff; background: $success; }
.cs-line { flex: 1; height: 1px; background: $gray-200; min-width: 20px; }
.cs-line-done { background: $success; }

/* ── Loading state ── */
.os-loading {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 4rem 0; gap: 1rem;
}
.os-loading-text { font-size: 14px; color: $gray-600; margin: 0; }

/* ── Success / error banner ── */
.os-banner {
  display: flex; align-items: center; gap: 14px;
  border-radius: $border-radius; padding: 1rem 1.25rem;
  margin-bottom: 1.25rem;
}
.os-banner-success { background: #f0faf4; border: 1px solid #c3e6cb; }
.os-banner-danger  { background: lighten($danger, 42%); border: 1px solid lighten($danger, 30%); }
.os-banner-icon {
  width: 36px; height: 36px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; color: #fff; flex-shrink: 0;
}
.os-icon-success { background: $success; }
.os-icon-danger  { background: $danger; }
.os-banner-title { font-size: 15px; font-weight: 500; }
.os-banner-success .os-banner-title { color: #1b5e20; }
.os-banner-danger  .os-banner-title { color: darken($danger, 10%); }
.os-banner-sub { font-size: 13px; color: #388e3c; margin-top: 2px; }

/* ── Section card ── */
.os-section {
  background: #fff; border: 1px solid $gray-200;
  border-radius: $border-radius; overflow: hidden;
}
.os-section-header {
  display: flex; align-items: center; gap: 8px;
  padding: .75rem 1rem; font-size: 13px; font-weight: 500;
  border-bottom: 1px solid $gray-200;
}
.os-header-success { background: #f0faf4; color: #1b5e20; }
.os-header-gray    { background: $gray-100; color: $gray-700; }
.os-count-badge {
  display: inline-flex; align-items: center; justify-content: center;
  width: 20px; height: 20px; border-radius: 50%;
  font-size: 11px; font-weight: 500; margin-left: auto;
}
.os-count-success { background: #c3e6cb; color: #1b5e20; }
.os-count-gray    { background: $gray-300; color: $gray-700; }

/* ── Product row ── */
.os-product-row {
  display: flex; gap: 1rem; align-items: flex-start;
  padding: 1rem; border-bottom: 1px solid $gray-100;
  &:last-child { border-bottom: none; }
}
.os-prod-img {
  display: block; width: 80px; min-width: 80px; height: 80px;
  border-radius: $border-radius-sm; overflow: hidden;
  background: $gray-100; border: 1px solid $gray-200; flex-shrink: 0;
  img {
    width: 100%; height: 100%;
    object-fit: contain; object-position: center; padding: 4px;
  }
}
.os-prod-body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 5px; }
.os-prod-name {
  font-size: 14px; font-weight: 500; color: $gray-900;
  text-decoration: none; line-height: 1.4;
  &:hover { color: $primary; text-decoration: none; }
}

/* ── Sent badge ── */
.os-sent-badge {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 12px; color: $success; background: #f0faf4;
  border: 1px solid #c3e6cb; border-radius: $border-radius-sm;
  padding: 3px 10px; width: fit-content;
}

/* ── Redemption box ── */
.os-redemption-box {
  background: $gray-100; border: 1px solid $gray-200;
  border-radius: $border-radius-sm; padding: 6px 12px; margin-top: 2px;
}
.os-redemption-label { font-size: 11px; color: $gray-500; margin-bottom: 1px; }
.os-redemption-val { font-size: 13px; font-weight: 600; color: $gray-900; word-break: break-all; }
.os-barcode-img { max-width: 120px; margin-top: 4px; }

/* ── Meta chips ── */
.os-meta-row { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 4px; }
.os-chip {
  display: inline-flex; align-items: center;
  background: $gray-100; border: 1px solid $gray-200;
  border-radius: $border-radius-sm; padding: 2px 10px;
  font-size: 12px; color: $gray-600;
}
.os-chip-value { color: $danger; background: lighten($danger, 42%); border-color: lighten($danger, 30%); font-weight: 600; }
.os-chip-order { color: $primary; background: lighten($primary, 44%); border-color: lighten($primary, 30%); }

/* ── Processing note ── */
.os-processing-note { font-size: 12.5px; color: $gray-600; line-height: 1.5; }
.os-tax-note { font-size: 11.5px; color: $gray-500; }

/* ── Continue button ── */
.os-continue-wrap { display: flex; justify-content: center; padding: 1rem 0; }
.os-continue-btn {
  display: inline-flex; align-items: center; gap: 8px;
  background: $primary; color: #fff; border: none;
  border-radius: $border-radius-sm; padding: 10px 24px;
  font-size: 14px; font-weight: 500; text-decoration: none;
  transition: background .15s ease;
  &:hover { background: darken($primary, 8%); color: #fff; text-decoration: none; }
}

/* ── Help section ── */
.os-help-section {
  background: $gray-100; border-radius: $border-radius;
  padding: 1.25rem; margin-top: .5rem; margin-bottom: 1.5rem;
}
.os-help-title { font-size: 14px; font-weight: 500; margin-bottom: .85rem; }
.os-tips-grid {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: .75rem; margin-bottom: 1rem;
}
.os-tips-list { display: flex; flex-direction: column; gap: .6rem; margin-bottom: .85rem; }
.os-tip-card {
  display: flex; align-items: flex-start; gap: 10px;
  background: #fff; border: 1px solid $gray-200;
  border-radius: $border-radius-sm; padding: .75rem;
  font-size: 12.5px; color: $gray-600; line-height: 1.5;
}
.os-tip-icon {
  width: 28px; height: 28px; border-radius: 50%; background: $gray-600;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 11px; flex-shrink: 0; margin-top: 1px;
}
.os-contact-card {
  background: #fff; border: 1px solid $gray-200;
  border-radius: $border-radius-sm; padding: .85rem 1rem;
  font-size: 13px; color: $gray-600; line-height: 1.7;
  a { color: $primary; }
}

/* ── Instructions collapse ── */
.instructions { background: #fff !important; font-size: 13px; }

/* ── Responsive ── */
@media (max-width: 576px) {
  .os-tips-grid { grid-template-columns: 1fr; }
  .os-prod-img { width: 64px; min-width: 64px; height: 64px; }
  .os-product-row { padding: .85rem; gap: .75rem; }
}

/* ── Keep existing modal style ── */
.modal-body { padding: 2rem 2rem 0 2rem !important; }

h3::after, h4::after, h5::after { display: none; }
</style>